import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Typography, Button, Paper } from '@mui/material';
import AUHeader from '../components/AUHeader';
import VideoAccessForm from '../components/VideoAccessForm';
import DescargaPDF from '../components/DescargaPDF';
import { hasValidVideoAccess, setStoredVideoAccess } from '../utils/videoAccessStorage';
import { submitLead } from '../utils/submitLead';

const BLUE = '#1A7FFF';
const BLUE_HOVER = '#1568D9';
const DARK = '#0F1117';
const GRAY = '#64748B';
const BORDER = '#E2E8F0';
const GRAY_LIGHT = '#F8FAFC';

const PDF_URL = '/recursos/tabla-equivalencias-au-coaching.pdf';

// Entregable del carrusel CR2 (comenta TABLA, 28-09). Mismo formato que /periodizacion:
// el PDF se muestra tal cual, embebido, y se puede descargar.
// En iOS el visor embebido a veces muestra solo la primera página: por eso el botón de
// descarga va arriba y abajo, y la nota bajo el visor.

const EquivalenciasPage = () => {
  const navigate = useNavigate();
  const [unlocked, setUnlocked] = useState(false);
  const [showLeadForm, setShowLeadForm] = useState(false);

  const handleUnlock = () => {
    if (hasValidVideoAccess()) {
      setUnlocked(true);
    } else {
      setShowLeadForm(true);
    }
  };

  const handleLeadSubmit = async (data) => {
    setShowLeadForm(false);
    setStoredVideoAccess(data);
    await submitLead({ data, source: 'equivalencias' });
    setUnlocked(true);
  };

  return (
    <Box sx={{ bgcolor: GRAY_LIGHT, fontFamily: 'Kanit, sans-serif', minHeight: '100vh' }}>
      <AUHeader />

      <Container maxWidth="lg" sx={{ pt: { xs: 4, md: 6 }, pb: { xs: 6, md: 10 } }}>
        {/* Hero */}
        <Typography sx={{
          fontSize: 15, fontWeight: 700, letterSpacing: 2.5, color: BLUE,
          textTransform: 'uppercase', mb: 0.75, fontFamily: 'Kanit, sans-serif',
        }}>
          Material gratis
        </Typography>
        <Typography component="h1" sx={{
          fontFamily: 'Kanit, sans-serif', fontWeight: 800,
          fontSize: { xs: '2rem', md: '2.8rem' }, color: DARK, mb: 1,
        }}>
          Tabla de Equivalencias
        </Typography>
        <Typography sx={{ color: GRAY, mb: 4, maxWidth: 680, fontFamily: 'Kanit, sans-serif' }}>
          Cambiar pollo por huevo "porque es lo mismo" es el error más común al hacer dieta. Esta tabla te dice
          cuánto de cada alimento equivale a una porción de su grupo, con los mismos valores que usamos en la app de
          AU para armar los planes de nuestros alumnos.
        </Typography>

        {!unlocked ? (
          /* Gate — todo el material vive detrás del form */
          <Paper elevation={0} sx={{
            borderRadius: 3, border: `1px solid ${BLUE}`, bgcolor: '#fff',
            p: { xs: 3, md: 5 }, textAlign: 'center', mt: 1,
          }}>
            <Typography sx={{ fontWeight: 800, color: DARK, fontFamily: 'Kanit, sans-serif', fontSize: { xs: '1.4rem', md: '1.9rem' }, mb: 1 }}>
              Desbloquea la tabla completa
            </Typography>
            <Typography sx={{ color: GRAY, fontFamily: 'Kanit, sans-serif', mb: 3, maxWidth: 560, mx: 'auto' }}>
              Déjanos tus datos y accede al instante a todo el material:
            </Typography>
            <Box sx={{ maxWidth: 500, mx: 'auto', textAlign: 'left', mb: 3.5 }}>
              {[
                'Proteínas, carbohidratos, frutas, vegetales y grasas: más de 80 alimentos de supermercado',
                'Un semáforo para ver de un vistazo cuál te da más comida por las mismas calorías',
                'Un cambio resuelto paso a paso, para hacerlo solo con cualquier alimento',
                'La tabla completa, para verla acá o descargarla',
              ].map((item) => (
                <Box key={item} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, mb: 1.25 }}>
                  <Box sx={{ color: BLUE, fontWeight: 800, fontFamily: 'Kanit, sans-serif', fontSize: 18, lineHeight: 1.35 }}>✓</Box>
                  <Typography sx={{ color: DARK, fontFamily: 'Kanit, sans-serif', fontSize: 15 }}>{item}</Typography>
                </Box>
              ))}
            </Box>
            <Button
              variant="contained" disableElevation onClick={handleUnlock}
              sx={{
                py: 1.5, px: 5, fontFamily: 'Kanit, sans-serif', fontWeight: 700, fontSize: 16,
                textTransform: 'none', bgcolor: BLUE, '&:hover': { bgcolor: BLUE_HOVER },
              }}
            >
              Ver la tabla completa
            </Button>
            <Typography sx={{ color: GRAY, fontFamily: 'Kanit, sans-serif', fontSize: 12.5, mt: 2 }}>
              Gratis. Solo te pedimos tus datos para enviártelo y acompañarte en el proceso.
            </Typography>
          </Paper>
        ) : (
        <>
        <DescargaPDF
          url={PDF_URL}
          titulo="Descarga la Tabla de Equivalencias en PDF"
          bajada="Seis páginas: cómo usarla, un cambio resuelto y los cinco grupos con su semáforo. Abajo la tienes completa."
        />

        {/* El PDF tal cual, embebido */}
        <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${BORDER}`, overflow: 'hidden', bgcolor: '#fff', mb: 2 }}>
          <Box
            component="iframe"
            src={`${PDF_URL}#view=FitH&toolbar=0`}
            title="Tabla de Equivalencias AU Coaching"
            sx={{ display: 'block', width: '100%', height: { xs: '70vh', md: '85vh' }, border: 0 }}
          />
        </Paper>
        <Typography sx={{ color: GRAY, fontFamily: 'Kanit, sans-serif', fontSize: 13, mb: 4 }}>
          Si en tu teléfono solo ves la primera página, descárgala con el botón: son seis páginas.
        </Typography>

        <Box sx={{ mb: 5 }}>
          <Button
            variant="contained" disableElevation component="a" href={PDF_URL} download
            sx={{
              py: 1.3, px: 3.5, fontFamily: 'Kanit, sans-serif', fontWeight: 700, fontSize: 15,
              textTransform: 'none', bgcolor: BLUE, '&:hover': { bgcolor: BLUE_HOVER },
            }}
          >
            Descargar PDF
          </Button>
        </Box>

        {/* CTA a coaching */}
        <Paper elevation={0} sx={{ mt: 5, p: { xs: 3, md: 4 }, borderRadius: 3, bgcolor: DARK }}>
          <Typography sx={{ color: '#fff', fontWeight: 800, fontFamily: 'Kanit, sans-serif', fontSize: { xs: 18, md: 22 }, mb: 0.75 }}>
            ¿Quieres que lo hagamos juntos?
          </Typography>
          <Typography sx={{ color: '#CBD5E1', fontFamily: 'Kanit, sans-serif', fontSize: 14.5, mb: 2.5, maxWidth: 640 }}>
            Una tabla te dice cuánto equivale cada alimento. En AU tu plan ya trae las equivalencias calculadas en la
            app, y cada semana revisamos tus números contigo para que el progreso no se corte.
          </Typography>
          <Button
            variant="contained" disableElevation onClick={() => navigate('/form')}
            sx={{
              py: 1.3, px: 3.5, fontFamily: 'Kanit, sans-serif', fontWeight: 700, fontSize: 15,
              textTransform: 'none', bgcolor: BLUE, '&:hover': { bgcolor: BLUE_HOVER },
            }}
          >
            Postula a AU Coaching
          </Button>
        </Paper>
        </>
        )}
      </Container>

      <VideoAccessForm
        open={showLeadForm}
        onClose={() => setShowLeadForm(false)}
        onSubmit={handleLeadSubmit}
        title="Desbloquea la tabla de equivalencias"
        subtitle="Déjanos tus datos y te mostramos la tabla completa al instante, con el PDF para descargar."
        submitLabel="Ver la tabla completa"
      />
    </Box>
  );
};

export default EquivalenciasPage;
